import { useState, useRef, useEffect, useCallback } from 'react';
import { api } from './hrApi';

const sleep = ms => new Promise(r => setTimeout(r, ms));

export default function CheckIn() {
  const [action, setAction] = useState('checkin');
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [geoNote, setGeoNote] = useState('Fetching location…');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const geoRef = useRef(null);

  const startCam = useCallback(async () => {
    try {
      if (!streamRef.current) {
        streamRef.current = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      }
      if (videoRef.current) videoRef.current.srcObject = streamRef.current;
    } catch {
      setMsg('Camera unavailable — check permissions / HTTPS.');
    }
  }, []);

  useEffect(() => {
    startCam();
    navigator.geolocation?.getCurrentPosition(
      p => {
        geoRef.current = { latitude: p.coords.latitude, longitude: p.coords.longitude,
                           accuracy: p.coords.accuracy, altitude: p.coords.altitude };
        const acc = Math.round(p.coords.accuracy);
        setGeoNote(acc > 500 ? `Location approximate (±${acc} m) — GPS unavailable` : `Location captured (±${acc} m)`);
      },
      () => { geoRef.current = null; setGeoNote('Location unavailable.'); },
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 }
    );
    return () => { streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null; };
  }, [startCam]);

  const capture = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return;
    setBusy(true); setMsg('Hold still…'); setMsgOk(false);
    const canvas = document.createElement('canvas');
    const images_b64 = [];
    for (let i = 0; i < 4; i++) {
      canvas.width = video.videoWidth; canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      images_b64.push(canvas.toDataURL('image/jpeg', 0.85));
      if (i < 3) await sleep(280);
    }
    try {
      const body = { images_b64, action, ...(geoRef.current || {}) };
      const d = await api('/checkin', { method: 'POST', body: JSON.stringify(body) });
      setMsg(d.message); setMsgOk(!!d.verified);
    } catch (e) { setMsg(e.message); setMsgOk(false); }
    setBusy(false);
  };

  const isIn = action === 'checkin';
  const accent = isIn ? '#0E9F8A' : '#D97706';

  return (
    <section className="page on">
      <h2 className="pagetitle display">Check In / Out</h2>
      <div className="pagesub">Your face is verified against your own profile before attendance is sent to Zoho People.</div>
      <div className="seg">
        <button className={isIn ? 'sel-in' : ''} onClick={() => setAction('checkin')}>Check In</button>
        <button className={!isIn ? 'sel-out' : ''} onClick={() => setAction('checkout')}>Check Out</button>
      </div>
      <div className="camwrap">
        <div className={`camring${busy ? ' scanning' : ''}`} style={{ '--ring': accent }}>
          <div className="camlens">
            <video ref={videoRef} autoPlay playsInline muted />
          </div>
        </div>
      </div>
      <button className="cap-btn" style={{ background: accent }} disabled={busy} onClick={capture}>
        {busy ? 'Verifying…' : '◉  Capture & Verify'}
      </button>
      <div id="ci-msg" className={msg ? (msgOk ? 'ok-text' : 'fail-text') : ''}>{msg}</div>
      <div id="geo-note">{geoNote}</div>
    </section>
  );
}
